/**
 * Tidal curve — a height-vs-time graph for the day for whichever height source is
 * active (Admiralty events, FES, or the RYA training port). It samples the
 * source's height function across the window, draws the water curve, marks the
 * HW/LW turning points, shows a "now" indicator, and lets you tap or drag along
 * it to read the height at any moment, the way you would off a tidal-curve
 * diagram. Source-agnostic: it only needs a height-at-time function.
 *
 * TIMES: every label on the curve is drawn in the PORT'S OWN time zone, set by
 * `tzOffsetMs` (the port's offset from UT in ms) and named by `zoneLabel`. The
 * height-at-time function and the window bounds stay in real UTC ms; only the
 * clock text is shifted, so the curve reads the same clock as the HW/LW list and
 * the almanac. Default (0 / "UT") keeps the older callers unchanged.
 *
 * The read-out cursor can be controlled from the parent (a time box, a "now"
 * button) via `cursorMs` + `onScrub`; left uncontrolled it keeps its own state.
 */
import { useMemo, useRef, useState } from 'react';
import { sampleCurve, findExtrema } from '@/tides/curve';
import { useUnitsStore, formatDepth } from '@/store/units';

const W = 360;
const H = 184;
const PAD_L = 34;
const PAD_R = 10;
const PAD_T = 12;
const PAD_B = 26;
const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;

export function TideCurve({
  heightAt,
  start,
  end,
  nowMs,
  tzOffsetMs = 0,
  zoneLabel = 'UT',
  cursorMs,
  onScrub,
}: {
  heightAt: (ms: number) => number;
  start: number;
  end: number;
  nowMs: number;
  /** The port's offset from UT in ms; shifts the CLOCK LABELS only. */
  tzOffsetMs?: number;
  /** Zone name shown against the times, e.g. "UT", "UT−1". */
  zoneLabel?: string;
  /** Controlled read-out cursor (real UTC ms), or null. Omit for internal state. */
  cursorMs?: number | null;
  /** Called as the user drags/taps along the curve, with the real UTC ms. */
  onScrub?: (ms: number | null) => void;
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [innerCursor, setInnerCursor] = useState<number | null>(null);
  const depthUnit = useUnitsStore((s) => s.depth);
  const controlled = onScrub != null;
  const cursor = controlled ? cursorMs ?? null : innerCursor;
  const setCursor = (ms: number | null) => (controlled ? onScrub?.(ms) : setInnerCursor(ms));

  // Local-clock formatter: shift the real instant into the port's zone, then read
  // the UTC parts of the shifted value so no device-local offset creeps in.
  const hhmm = (ms: number): string => {
    const d = new Date(ms + tzOffsetMs);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
  };

  const { samples, markers, hMin, hMax } = useMemo(() => {
    const s = sampleCurve(heightAt, start, end, 10 * 60_000).filter((p) => Number.isFinite(p.h));
    const m = findExtrema(heightAt, start, end);
    let lo = Infinity;
    let hi = -Infinity;
    for (const p of s) {
      lo = Math.min(lo, p.h);
      hi = Math.max(hi, p.h);
    }
    if (!Number.isFinite(lo)) {
      lo = 0;
      hi = 1;
    }
    const padH = Math.max(0.3, (hi - lo) * 0.12);
    return { samples: s, markers: m, hMin: lo - padH, hMax: hi + padH };
  }, [heightAt, start, end]);

  const xFor = (t: number) => PAD_L + ((t - start) / (end - start)) * PLOT_W;
  const yFor = (h: number) => PAD_T + (1 - (h - hMin) / (hMax - hMin)) * PLOT_H;

  const curveD = samples.map((p, i) => `${i === 0 ? 'M' : 'L'}${xFor(p.t).toFixed(1)},${yFor(p.h).toFixed(1)}`).join(' ');
  const waterD = `${curveD} L${xFor(end).toFixed(1)},${(PAD_T + PLOT_H).toFixed(1)} L${xFor(start).toFixed(1)},${(PAD_T + PLOT_H).toFixed(1)} Z`;

  // x ticks every 6 h of the port-local day, y ticks low/mid/high.
  const xticks: number[] = [];
  for (let t = start; t <= end + 1; t += 6 * 3_600_000) xticks.push(t);
  const yticks = [hMin, (hMin + hMax) / 2, hMax];

  const readMs = cursor ?? (nowMs >= start && nowMs <= end ? nowMs : null);
  const readH = readMs != null ? heightAt(readMs) : null;

  function onMove(e: React.PointerEvent<SVGSVGElement>) {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const xPx = ((e.clientX - rect.left) / rect.width) * W;
    const frac = Math.min(1, Math.max(0, (xPx - PAD_L) / PLOT_W));
    setCursor(start + frac * (end - start));
  }

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label={`Tidal curve for the day, times in ${zoneLabel}`}
      style={{ touchAction: 'none', display: 'block', color: 'inherit' }}
      onPointerDown={onMove}
      onPointerMove={(e) => e.buttons && onMove(e)}
      onPointerLeave={() => !controlled && setCursor(null)}
    >
      {/* y grid + labels */}
      {yticks.map((h, i) => (
        <g key={`y${i}`}>
          <line x1={PAD_L} y1={yFor(h)} x2={W - PAD_R} y2={yFor(h)} stroke="currentColor" strokeOpacity={0.12} />
          <text x={PAD_L - 4} y={yFor(h) + 3} textAnchor="end" fontSize={9} fill="currentColor" fillOpacity={0.6}>
            {h.toFixed(1)}
          </text>
        </g>
      ))}
      {/* x ticks + labels (port-local hour) */}
      {xticks.map((t, i) => (
        <g key={`x${i}`}>
          <line x1={xFor(t)} y1={PAD_T} x2={xFor(t)} y2={PAD_T + PLOT_H} stroke="currentColor" strokeOpacity={0.08} />
          <text x={xFor(t)} y={H - 9} textAnchor="middle" fontSize={9} fill="currentColor" fillOpacity={0.6}>
            {hhmm(t).slice(0, 2)}
          </text>
        </g>
      ))}
      {/* water fill + curve */}
      <path d={waterD} fill="#3a7bd5" fillOpacity={0.16} stroke="none" />
      <path d={curveD} fill="none" stroke="#3a7bd5" strokeWidth={1.8} />
      {/* HW/LW markers */}
      {markers.map((m, i) => (
        <g key={`m${i}`}>
          <circle cx={xFor(m.t)} cy={yFor(m.heightM)} r={2.4} fill="#3a7bd5" />
          <text
            x={xFor(m.t)}
            y={yFor(m.heightM) + (m.type === 'H' ? -6 : 12)}
            textAnchor="middle"
            fontSize={8.5}
            fill="currentColor"
            fillOpacity={0.8}
          >
            {m.type === 'H' ? 'HW' : 'LW'} {hhmm(m.t)} · {m.heightM.toFixed(1)}m
          </text>
        </g>
      ))}
      {/* timezone caption: every time on this curve is the port's own clock. */}
      <text x={W - PAD_R} y={PAD_T + 2} textAnchor="end" fontSize={8.5} fill="currentColor" fillOpacity={0.55}>
        times {zoneLabel}
      </text>
      {/* now marker */}
      {nowMs >= start && nowMs <= end && (
        <g>
          <line x1={xFor(nowMs)} y1={PAD_T} x2={xFor(nowMs)} y2={PAD_T + PLOT_H} stroke="#e8722e" strokeWidth={1.2} />
          <circle cx={xFor(nowMs)} cy={yFor(heightAt(nowMs))} r={3} fill="#e8722e" />
        </g>
      )}
      {/* interactive read-out cursor */}
      {readMs != null && readH != null && Number.isFinite(readH) && (
        <g>
          {cursor != null && (
            <line x1={xFor(readMs)} y1={PAD_T} x2={xFor(readMs)} y2={PAD_T + PLOT_H} stroke="currentColor" strokeOpacity={0.45} strokeDasharray="3 3" />
          )}
          <text x={PAD_L} y={PAD_T - 2} textAnchor="start" fontSize={9.5} fill="currentColor" fontWeight={600}>
            {hhmm(readMs)} {zoneLabel} · {formatDepth(readH, depthUnit)}
          </text>
        </g>
      )}
    </svg>
  );
}
